import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { secureLocalStorage } from '../utils/crypto';

const TokenContext = createContext({});

export const TokenProvider = ({ children }) => {
  const [idToken, setIdToken] = useState(() => {
    return secureLocalStorage.getItem('idToken');
  });

  const [tokenVersion, setTokenVersion] = useState(0);

  const updateToken = useCallback((newToken) => {
    if (newToken !== idToken) {
      setIdToken(newToken);
      setTokenVersion(prev => prev + 1);
    }
  }, [idToken]);

  const clearToken = useCallback(() => {
    setIdToken(null);
    setTokenVersion(prev => prev + 1);
  }, []);

  useEffect(() => {
    secureLocalStorage.setTokenUpdateCallback(updateToken);
    
    return () => {
      secureLocalStorage.removeTokenUpdateCallback();
    };
  }, [updateToken]);

  // Listener para mudanças no localStorage (para múltiplas abas)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'idToken') {
        const newToken = secureLocalStorage.getItem('idToken');
        updateToken(newToken);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [updateToken]);

  // Expor métodos para outros componentes atualizarem o token
  const setToken = useCallback((token) => {
    if (token) {
      secureLocalStorage.setItem('idToken', token);
    } else {
      secureLocalStorage.removeItem('idToken');
    }
    updateToken(token);
  }, [updateToken]);

  const removeToken = useCallback(() => {
    secureLocalStorage.removeItem('idToken');
    clearToken();
  }, [clearToken]);

  return (
    <TokenContext.Provider
      value={{
        idToken,
        tokenVersion,
        setToken,
        removeToken,
        updateToken,
        clearToken
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export const useToken = () => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useToken must be used within a TokenProvider');
  }
  return context;
}; 
